import { Box, Stack, Typography, useMediaQuery } from '@mui/material';
import * as Constant from 'common/Constant';
import _ from 'lodash';
import { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { store } from 'store/store';
import * as Action from '../favoriteReport/actions/action';
import ReportChip from '../component/reportFilter';
import { ADHOC_REPORT_CATEGORIES } from 'common/Constant';
import { IconX } from '@tabler/icons-react';

const reportTags = ADHOC_REPORT_CATEGORIES;

// Helper function to safely extract reportTag name from report object
// Handles both string format (API: "Team", "test1") and object format (legacy: { name: "Team", code: "..." })
const getReportTagName = (report) => {
  const tag = report?.reportTag;
  if (typeof tag === 'string') {
    return tag;
  }
  return typeof tag === 'object' ? tag?.name : null;
};

// Normalize tag values for comparison: trim, lowercase, handle null/undefined
const normalizeTag = (value) => {
  return String(value ?? '').trim().toLowerCase();
};

// Derive unique subfolders from a report list (Team or Personal).
// Returns array of { code: normalizedName, name: displayName } objects.
// Root-level reports (rootTagName, e.g. "Team" or "Personal") are grouped
// under the "Uncategorized" chip.
const deriveTeamSubfolders = (reports, rootTagName = 'team') => {
  const rootCode = normalizeTag(rootTagName);
  const uniqueTags = new Map();
  let hasRootReports = false;

  (Array.isArray(reports) ? reports : []).forEach((report) => {
    const displayName = String(getReportTagName(report) ?? '').trim();
    const code = normalizeTag(displayName);
    if (!code || code === rootCode) {
      hasRootReports = true;
      return;
    }
    if (uniqueTags.has(code)) {
      return;
    }
    uniqueTags.set(code, { code, name: displayName });
  });

  if (hasRootReports) {
    uniqueTags.set('uncategorized', {
      code: 'uncategorized',
      name: 'Uncategorized'
    });
  }

  return Array.from(uniqueTags.values());
};

const SubfolderFilters = ({
  reports = [],
  selectedSubfolders = [],
  onChange,
  filter,
  rootTagName = 'team'
}) => {
  const isMobileView = useMediaQuery('(max-width:767px)');
  const rootCode = normalizeTag(rootTagName);

  const teamSubfolders = useMemo(
    () => deriveTeamSubfolders(reports, rootTagName),
    [reports, rootTagName]
  );

  const subfolderTotals = useMemo(() => {
    const counts = {};
    (Array.isArray(reports) ? reports : []).forEach((report) => {
      const code = normalizeTag(getReportTagName(report));
      if (!code || code === rootCode) {
        counts.uncategorized = (counts.uncategorized ?? 0) + 1;
        return;
      }
      counts[code] = (counts[code] ?? 0) + 1;
    });
    return counts;
  }, [reports, rootCode]);
  const safeSelectedSubfolders = Array.isArray(selectedSubfolders)
    ? selectedSubfolders
    : [];
  const selectedCodes = safeSelectedSubfolders.map(
    (subfolder) => subfolder.code
  );
  const toggleSubfolder = (subfolder) => {
    const alreadySelected = selectedCodes.includes(subfolder.code);
    const updatedSelection = alreadySelected
      ? safeSelectedSubfolders.filter(
          (item) => item.code !== subfolder.code
        )
      : [...safeSelectedSubfolders, subfolder];
    onChange?.(updatedSelection);
  };
  // Hide the chip row entirely when Uncategorized is the only subfolder,
  // so all root-level reports show without requiring a filter selection.
  const isOnlyUncategorized =
    teamSubfolders.length === 1 && teamSubfolders[0].code === 'uncategorized';

  // A stale selection (e.g. from before a tenant switch) would otherwise keep
  // filtering root-level reports out even though there's no chip left to clear it.
  useEffect(() => {
    if (isOnlyUncategorized && safeSelectedSubfolders.length > 0) {
      onChange?.([]);
    }
  }, [isOnlyUncategorized, safeSelectedSubfolders.length, onChange]);

  if (!teamSubfolders.length || isOnlyUncategorized) {
    return null;
  }

  return (
    <Stack
      direction={isMobileView ? 'column' : 'row'}
      sx={{
        gap: '8px',
        flexWrap: isMobileView ? 'nowrap' : 'wrap',
        alignItems: isMobileView ? 'stretch' : 'center',
        width: '100%',
        mb: '24px'
      }}
    >
      {teamSubfolders.map((subfolder) => {
        const selected = selectedCodes.includes(subfolder.code);
        return (
          <ReportChip
            key={`${rootTagName}-subfolder-${subfolder.code}`}
            item={subfolder}
            filter={filter}
            total={subfolderTotals[subfolder.code] ?? 0}
            selected={selected}
            onToggle={() => toggleSubfolder(subfolder)}
            isMobile={isMobileView}
            chipWidth={isMobileView ? '100%' : 'auto'}
          />
        );
      })}
      {safeSelectedSubfolders.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            color: '#2F54A5',
            height: '24px',
            cursor: 'pointer',
            '&:hover': {
              color: '#068DF1'
            }
          }}
          onClick={() => onChange?.([])}
        >
          <IconX height={16} width={16} />
          <Typography
            sx={{
                cursor: 'pointer',
                fontFamily: 'Noto Sans',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 600,
                height: '24px',
                lineHeight: '150%',
                width: '57px',
                letterSpacing: '0',
                marginTop: '2px'
              }}
          >
            Clear All
          </Typography>
        </Box>
      )}
    </Stack>
  );
};

export const TeamSubfolderFilters = (props) => (
  <SubfolderFilters {...props} rootTagName="team" />
);

export const PersonalSubfolderFilters = (props) => (
  <SubfolderFilters {...props} rootTagName="personal" />
);


const ReportFilters = ({ setData, filter, onSelectionChange, resetSelectionTrigger }) => {
  const adHocPublic = useSelector((state) => state.customReport.adHocPublic);
  const adHocPersonal = useSelector((state) => state.customReport.adHocPersonal);
  const currentMenu = useSelector((state) => state.favoriteReport.currentMenu);
  const [showClearAll,  setShowClearAll] = useState(false);
  const [totals, updateTotals] = useState({});
  const sortedAdHocReports = useMemo(() => {
    const personalReports = Array.isArray(adHocPersonal)
      ? adHocPersonal
      : [];
    const teamReports = Array.isArray(adHocPublic) ? adHocPublic : [];
    return [
      ...personalReports,
      ...teamReports
    ];
  }, [adHocPublic, adHocPersonal]);


  const [selectedCodes, setSelectedCodes] = useState(() => []);
  const isMobileView = useMediaQuery('(max-width:767px)');

  useEffect(() => {
    updateTotals(_.groupBy(sortedAdHocReports, (item) => item.category));
  }, [sortedAdHocReports]);

  useEffect(() => {
    setSelectedCodes([]);
    setShowClearAll(false);
  }, [resetSelectionTrigger]);

  useEffect(() => {
    if (!Array.isArray(sortedAdHocReports)) {
      setData([]);
      return;
    }
    const filtered =
      selectedCodes.length > 0
        ? sortedAdHocReports.filter((item) => selectedCodes.includes(item?.category))
        : sortedAdHocReports;
    setData(filtered);
  }, [selectedCodes, sortedAdHocReports, setData]);

  useEffect(() => {
    if (currentMenu?.name === Constant.ALL) {
      setSelectedCodes([]);
    }
  }, [currentMenu]);

  useEffect(() => {
    if (selectedCodes.length > 0 && currentMenu?.name === Constant.ALL) {
      store.dispatch(Action.setCurrentMenuItem({ name: null }));
    }
  }, [selectedCodes, currentMenu]);

  useEffect(() => {
      setShowClearAll(!isMobileView && selectedCodes.length > 0 );
  }, [selectedCodes, isMobileView]);

  useEffect(() => {
    if (typeof onSelectionChange === 'function') {
      onSelectionChange(reportTags.filter((t) => selectedCodes.includes(t?.code)));
    }
  }, [selectedCodes, onSelectionChange]);

  const toggleCode = (code) =>
    setSelectedCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );

  const clearAllFilters = () => setSelectedCodes([]);

  const getChipWidth = () => {
    if (isMobileView) return '100%';
    return 'auto';
  };

  return (   
    <>
      <Stack
        direction={isMobileView ? 'column' : 'row'}
        columnGap='12px'
        rowGap='12px'      
        sx={{
          alignItems: isMobileView ? 'flex-start' : 'center',
          ml: 'auto',
          flexWrap: isMobileView ? 'nowrap' : 'wrap',
          width: '100%',
          minHeight: 'auto'
        }}
      >
        {/* Render top-level category chips (Personal, Team) */}
        {reportTags.map((item) => {
          const code = item?.code;
          const isSelected = selectedCodes.includes(code);
          return (
            <ReportChip
              key={code}
              item={item}
              filter={filter}
              total={totals[item.code]?.length ?? 0}
              selected={isSelected}
              onToggle={() => toggleCode(code)}
              isMobile={isMobileView}
              chipWidth={getChipWidth()}
            />
          );
        })}
        {showClearAll && selectedCodes.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap:'4px', 
            color: '#2F54A5', 
            marginTop:'5px',
            height: '24px',
            '&:hover': {
              color: '#068DF1'
            } }}
            onClick={clearAllFilters}
          >
            <IconX
              height={16} 
              width={16} 
              style={{cursor:'pointer'}} 
            />
            <Typography
              sx={{ 
                cursor: 'pointer',
                fontFamily: 'Noto Sans', 
                fontSize: '14px', 
                fontStyle: 'normal', 
                fontWeight: 600, 
                height: '24px',
                lineHeight: '150%',
                width: '57px', 
                letterSpacing: '0',
                marginTop: '2px'
              }}>
              Clear All
            </Typography>
          </Box>
        )}
      </Stack>
    </>
  );
};
export default ReportFilters;